import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  acceptPartnerRequest,
  cancelPartnerRequest,
  confirmTurnCoinPaid,
  confirmTurnDareDone,
  chooseTurnPenaltyMode,
  getCurrentPenalty,
  getCurrentTurn,
  getGameLobby,
  getGameLeaderboard,
  getMyGameHistory,
  getMyGameStats,
  getMyPartnerInbox,
  isGameGuestUnauthorized,
  logoutGamePlayer,
  rejectPartnerRequest,
  sendPartnerRequest,
  submitTurnAnswer,
  submitTurnGuess,
} from "../../features/game/gameApi";
import GameLobbyPlayerCard from "../../components/game/GameLobbyPlayerCard";
import GameStatusBanner from "../../components/game/GameStatusBanner";
import GameReconnectBanner from "../../components/game/GameReconnectBanner";
import GameMobileTabs from "../../components/game/GameMobileTabs";
import PartnerRequestInbox from "../../components/game/PartnerRequestInbox";
import PartnerRequestModal from "../../components/game/PartnerRequestModal";
import GameTurnStage from "../../components/game/GameTurnStage";
import MyGameStatsCard from "../../components/game/MyGameStatsCard";
import GameLeaderboardCard from "../../components/game/GameLeaderboardCard";
import GameHistoryList from "../../components/game/GameHistoryList";
import useGameSocket from "../../hooks/useGameSocket";
import useGameRecovery from "../../hooks/useGameRecovery";
import {
  clearGameSession,
  getGameEntry,
  getGameGuestToken,
  getGamePlayer,
  setGamePlayer,
} from "../../features/game/gameSession";
import cls from "./GameLobbyPage.module.scss";

function getFriendlyPartnerLabel(player) {
  if (!player) return "single";
  if (player.partnerStatus === "partnered" && player.partner?.displayName) {
    return `partnered with ${player.partner.displayName}`;
  }
  return player.partnerStatus || "single";
}

export default function GameLobbyPage() {
  const navigate = useNavigate();
  const { gameId } = useParams();

  const [lobby, setLobby] = useState(null);
  const [inbox, setInbox] = useState({
    inboundPendingRequests: [],
    outboundPendingRequests: [],
    currentPartner: null,
  });
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [currentTurnView, setCurrentTurnView] = useState(null);
  const [turnLoading, setTurnLoading] = useState(false);
  const [penaltyView, setPenaltyView] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myHistory, setMyHistory] = useState([]);
  const [mobileTab, setMobileTab] = useState("play");
  const [refreshing, setRefreshing] = useState(false);

  const gameToken = getGameGuestToken();
  const recovery = useGameRecovery({
    gameId,
    gameToken,
    navigate,
    includeStats: true,
  });

  const gameEntry = getGameEntry();
  const storedPlayer = getGamePlayer();
  const storedPlayerId = storedPlayer?._id ? String(storedPlayer._id) : "";

  const handleStaleSession = useCallback(
    (message = "Your guest game session expired. Please rejoin the lobby.") => {
      clearGameSession();
      toast.error(message);
      navigate("/game/enter", {
        replace: true,
        state: { message },
      });
    },
    [navigate],
  );

  const loadLobbyData = useCallback(async () => {
    if (!gameId || !gameToken) return null;
    const data = await getGameLobby(gameId, gameToken);
    setLobby(data);
    if (data?.me) {
      setGamePlayer(data.me);
    }
    return data;
  }, [gameId, gameToken]);

  const loadInboxData = useCallback(async () => {
    if (!gameId || !gameToken) return null;
    const data = await getMyPartnerInbox(gameId, gameToken);
    setInbox({
      inboundPendingRequests: data?.inboundPendingRequests || [],
      outboundPendingRequests: data?.outboundPendingRequests || [],
      currentPartner: data?.currentPartner || null,
    });
    return data;
  }, [gameId, gameToken]);

  const loadCurrentTurnView = useCallback(async () => {
    if (!gameId || !gameToken) return null;
    const data = await getCurrentTurn(gameId, gameToken);
    setCurrentTurnView(data || null);
    return data;
  }, [gameId, gameToken]);

  const loadMyStats = useCallback(async () => {
    if (!gameId || !gameToken) return null;
    const data = await getMyGameStats(gameId, gameToken);
    setMyStats(data?.stats || null);
    return data;
  }, [gameId, gameToken]);

  const loadLeaderboard = useCallback(async () => {
    if (!gameId || !gameToken) return null;
    const data = await getGameLeaderboard(gameId, gameToken);
    setLeaderboard(data?.leaderboard || []);
    return data;
  }, [gameId, gameToken]);

  const loadMyHistory = useCallback(async () => {
    if (!gameId || !gameToken) return null;
    const data = await getMyGameHistory(gameId, gameToken, {
      page: 1,
      limit: 10,
    });
    setMyHistory(data?.items || []);
    return data;
  }, [gameId, gameToken]);

  const loadPenaltyView = useCallback(async () => {
    if (!gameId || !gameToken) return null;
    const data = await getCurrentPenalty(gameId, gameToken);
    setPenaltyView(data || null);
    return data;
  }, [gameId, gameToken]);

  useEffect(() => {
    if (!gameToken) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const run = async () => {
      try {
        await Promise.all([
          loadLobbyData(),
          loadInboxData(),
          loadCurrentTurnView(),
          loadPenaltyView(),
          loadMyStats(),
          loadLeaderboard(),
          loadMyHistory(),
        ]);
      } catch (error) {
        if (mounted && isGameGuestUnauthorized(error)) {
          handleStaleSession(
            "Your guest session is no longer valid. Please enter the game again.",
          );
          return;
        }

        if (mounted) {
          toast.error(
            error?.response?.data?.message ||
              "We could not load the latest lobby state.",
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [
    gameToken,
    handleStaleSession,
    loadCurrentTurnView,
    loadInboxData,
    loadLobbyData,
    loadPenaltyView,
    loadMyStats,
    loadLeaderboard,
    loadMyHistory,
  ]);

  const handleChoosePenaltyMode = useCallback(
    async (mode) => {
      if (!gameId || !gameToken || !currentTurnView?.turn?._id) return;
      setTurnLoading(true);
      try {
        await chooseTurnPenaltyMode(
          gameId,
          currentTurnView.turn._id,
          mode,
          gameToken,
        );
        await Promise.all([loadCurrentTurnView(), loadPenaltyView()]);
      } catch (error) {
        if (isGameGuestUnauthorized(error)) {
          handleStaleSession();
          return;
        }
        toast.error(
          error?.response?.data?.message || "Could not choose penalty mode.",
        );
      } finally {
        setTurnLoading(false);
      }
    },
    [
      currentTurnView?.turn?._id,
      gameId,
      gameToken,
      handleStaleSession,
      loadCurrentTurnView,
      loadPenaltyView,
    ],
  );

  const handleConfirmCoinPaid = useCallback(async () => {
    if (!gameId || !gameToken || !currentTurnView?.turn?._id) return;
    setTurnLoading(true);
    try {
      await confirmTurnCoinPaid(gameId, currentTurnView.turn._id, gameToken);
      await Promise.all([
        loadCurrentTurnView(),
        loadPenaltyView(),
        loadLobbyData(),
      ]);
    } catch (error) {
      if (isGameGuestUnauthorized(error)) {
        handleStaleSession();
        return;
      }
      toast.error(
        error?.response?.data?.message || "Could not confirm coin payment.",
      );
    } finally {
      setTurnLoading(false);
    }
  }, [
    currentTurnView?.turn?._id,
    gameId,
    gameToken,
    handleStaleSession,
    loadCurrentTurnView,
    loadLobbyData,
    loadPenaltyView,
  ]);

  const handleConfirmDareDone = useCallback(async () => {
    if (!gameId || !gameToken || !currentTurnView?.turn?._id) return;
    setTurnLoading(true);
    try {
      await confirmTurnDareDone(gameId, currentTurnView.turn._id, gameToken);
      await Promise.all([
        loadCurrentTurnView(),
        loadPenaltyView(),
        loadLobbyData(),
      ]);
    } catch (error) {
      if (isGameGuestUnauthorized(error)) {
        handleStaleSession();
        return;
      }
      toast.error(
        error?.response?.data?.message || "Could not confirm dare completion.",
      );
    } finally {
      setTurnLoading(false);
    }
  }, [
    currentTurnView?.turn?._id,
    gameId,
    gameToken,
    handleStaleSession,
    loadCurrentTurnView,
    loadLobbyData,
    loadPenaltyView,
  ]);

  const refreshInboxSilently = useCallback(async () => {
    try {
      await loadInboxData();
    } catch (error) {
      if (isGameGuestUnauthorized(error)) {
        handleStaleSession();
      }
    }
  }, [handleStaleSession, loadInboxData]);

  const mergeIncomingPartnerRequest = useCallback((payload) => {
    setInbox((prev) => {
      if (!payload?.request?._id) return prev;

      const request = payload.request;
      const requestId = String(request._id);
      const inbound = prev.inboundPendingRequests || [];
      const hasRequest = inbound.some((item) => String(item._id) === requestId);

      if (hasRequest) return prev;

      return {
        ...prev,
        inboundPendingRequests: [request, ...inbound],
      };
    });
  }, []);

  const applyPartnerRequestRealtimeUpdate = useCallback(
    (payload) => {
      setInbox((prev) => {
        if (!payload?.request?._id) return prev;

        const requestId = String(payload.request._id);
        const action = payload?.action;
        const myPlayerId = String(lobby?.me?._id || storedPlayerId || "");
        if (!myPlayerId) return prev;

        const isInbound =
          String(payload.request.toPlayerId || "") === myPlayerId;
        const isOutbound =
          String(payload.request.fromPlayerId || "") === myPlayerId;

        const removeRequest = (list = []) =>
          list.filter((item) => String(item._id) !== requestId);

        const upsertRequest = (list = []) => {
          const exists = list.some((item) => String(item._id) === requestId);
          if (exists) return list;
          return [payload.request, ...list];
        };

        if (action === "created") {
          return {
            ...prev,
            inboundPendingRequests: isInbound
              ? upsertRequest(prev.inboundPendingRequests)
              : prev.inboundPendingRequests || [],
            outboundPendingRequests: isOutbound
              ? upsertRequest(prev.outboundPendingRequests)
              : prev.outboundPendingRequests || [],
          };
        }

        if (["accepted", "rejected", "cancelled"].includes(action)) {
          return {
            ...prev,
            inboundPendingRequests: removeRequest(prev.inboundPendingRequests),
            outboundPendingRequests: removeRequest(
              prev.outboundPendingRequests,
            ),
            currentPartner:
              action === "accepted" && isInbound
                ? payload.players?.fromPlayer || prev.currentPartner
                : action === "accepted" && isOutbound
                  ? payload.players?.toPlayer || prev.currentPartner
                  : prev.currentPartner,
          };
        }

        return prev;
      });
    },
    [lobby?.me?._id, storedPlayerId],
  );

  const { connectionStatus } = useGameSocket({
    gameToken,
    gameId,
    onLobbyUpdated: (nextLobby) => {
      setLobby((prev) => ({ ...prev, ...nextLobby }));
      if (nextLobby?.me) {
        setGamePlayer(nextLobby.me);
      }
    },
    onPartnerRequestReceived: (payload) => {
      applyPartnerRequestRealtimeUpdate({
        action: "created",
        request: payload?.request,
      });
      mergeIncomingPartnerRequest(payload);
    },
    onPartnerRequestUpdated: async (payload) => {
      applyPartnerRequestRealtimeUpdate(payload);
      await refreshInboxSilently();
      if (payload?.action === "accepted") {
        toast.success("Partnership confirmed.");
      }
    },
    onPhaseUpdated: async (nextPhase) => {
      setLobby((prev) =>
        prev
          ? {
              ...prev,
              liveState: nextPhase,
            }
          : prev,
      );
      await Promise.all([loadCurrentTurnView(), loadPenaltyView()]);
    },
    onTurnUpdated: async () => {
      await Promise.all([loadCurrentTurnView(), loadPenaltyView()]);
    },
    onTurnResultReady: async () => {
      await Promise.all([loadCurrentTurnView(), loadPenaltyView()]);
    },
    onRecoveryUpdated: async () => {
      await Promise.all([
        loadLobbyData(),
        loadCurrentTurnView(),
        loadPenaltyView(),
      ]);
    },
    onLeaderboardUpdated: async () => {
      await Promise.all([loadLeaderboard(), loadMyStats(), loadMyHistory()]);
    },
    onPenaltyUpdated: async () => {
      await Promise.all([loadCurrentTurnView(), loadPenaltyView()]);
    },
    onRoundUpdated: async () => {
      await Promise.all([
        loadLobbyData(),
        loadCurrentTurnView(),
        loadPenaltyView(),
      ]);
    },
  });

  const currentPlayer = useMemo(() => {
    if (lobby?.me?._id) return lobby.me;
    if (!storedPlayer?._id) return null;

    return (
      lobby?.players?.find((player) => player._id === storedPlayer._id) || null
    );
  }, [lobby, storedPlayer?._id]);

  const otherPlayers = useMemo(() => {
    const meId = currentPlayer?._id;
    return (lobby?.players || []).filter((player) => player._id !== meId);
  }, [currentPlayer?._id, lobby?.players]);

  const availableSinglePlayers = useMemo(
    () =>
      otherPlayers.filter(
        (player) => player?.isActive && player?.partnerStatus === "single",
      ),
    [otherPlayers],
  );

  const activeCount = useMemo(
    () => (lobby?.players || []).filter((player) => player.isActive).length,
    [lobby?.players],
  );

  const totalCount = lobby?.players?.length || 0;
  const currentAvatar = currentPlayer?.image?.url || "";
  const currentInitial = (currentPlayer?.displayName || "?")
    .slice(0, 1)
    .toUpperCase();

  const canSendRequest =
    currentPlayer?.partnerStatus !== "partnered" &&
    !(inbox?.outboundPendingRequests || []).length &&
    !(inbox?.inboundPendingRequests || []).length;

  const handleLogout = async () => {
    if (!gameToken || !gameId) {
      clearGameSession();
      navigate("/game/enter", { replace: true });
      return;
    }

    setLoggingOut(true);
    try {
      await logoutGamePlayer(gameId, gameToken);
      clearGameSession();
      toast.success("You have left the game lobby.");
      navigate("/game/enter", {
        replace: true,
        state: { message: "You logged out of the game lobby." },
      });
    } catch (error) {
      clearGameSession();
      toast.error(
        error?.response?.data?.message ||
          "We could not confirm logout, but your local game session was cleared.",
      );
      navigate("/game/enter", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  const handleSendPartnerRequest = async (payload) => {
    if (!gameId || !gameToken) return false;

    setRequestSubmitting(true);
    try {
      await sendPartnerRequest(gameId, payload, gameToken);
      await Promise.all([loadLobbyData(), loadInboxData()]);
      toast.success("Partner request sent.");
      return true;
    } catch (error) {
      if (isGameGuestUnauthorized(error)) {
        handleStaleSession();
        return false;
      }

      toast.error(
        error?.response?.data?.message ||
          "We could not send that partner request.",
      );
      return false;
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handleInboxAction = async (request, action) => {
    if (!gameId || !gameToken || !request?._id) return;

    setActionLoadingId(request._id);
    try {
      if (action === "accept") {
        await acceptPartnerRequest(gameId, request._id, gameToken);
      }
      if (action === "reject") {
        await rejectPartnerRequest(gameId, request._id, gameToken);
      }
      if (action === "cancel") {
        await cancelPartnerRequest(gameId, request._id, gameToken);
      }

      await Promise.all([loadLobbyData(), loadInboxData()]);
      const label =
        action === "accept"
          ? "accepted"
          : action === "reject"
            ? "rejected"
            : "cancelled";
      toast.success(`Partner request ${label}.`);
    } catch (error) {
      if (isGameGuestUnauthorized(error)) {
        handleStaleSession();
        return;
      }

      toast.error(
        error?.response?.data?.message ||
          "We could not update that partner request.",
      );
    } finally {
      setActionLoadingId("");
    }
  };

  const handleSubmitAnswer = useCallback(
    async (selection) => {
      if (!gameId || !gameToken || !currentTurnView?.turn?._id) return;
      setTurnLoading(true);
      try {
        await submitTurnAnswer(
          gameId,
          currentTurnView.turn._id,
          selection,
          gameToken,
        );
        await loadCurrentTurnView();
      } catch (error) {
        if (isGameGuestUnauthorized(error)) {
          handleStaleSession();
          return;
        }
        toast.error(
          error?.response?.data?.message || "Could not submit answer.",
        );
      } finally {
        setTurnLoading(false);
      }
    },
    [
      currentTurnView?.turn?._id,
      gameId,
      gameToken,
      handleStaleSession,
      loadCurrentTurnView,
    ],
  );

  const handleSubmitGuess = useCallback(
    async (selection) => {
      if (!gameId || !gameToken || !currentTurnView?.turn?._id) return;
      setTurnLoading(true);
      try {
        await submitTurnGuess(
          gameId,
          currentTurnView.turn._id,
          selection,
          gameToken,
        );
        await loadCurrentTurnView();
      } catch (error) {
        if (isGameGuestUnauthorized(error)) {
          handleStaleSession();
          return;
        }
        toast.error(
          error?.response?.data?.message || "Could not submit guess.",
        );
      } finally {
        setTurnLoading(false);
      }
    },
    [
      currentTurnView?.turn?._id,
      gameId,
      gameToken,
      handleStaleSession,
      loadCurrentTurnView,
    ],
  );

  const handleRefreshLobby = useCallback(async () => {
    if (!gameId || !gameToken || refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([
        loadLobbyData(),
        loadInboxData(),
        loadCurrentTurnView(),
        loadPenaltyView(),
        loadMyStats(),
        loadLeaderboard(),
        loadMyHistory(),
      ]);
      toast.success("Lobby refreshed.");
    } catch (error) {
      if (isGameGuestUnauthorized(error)) {
        handleStaleSession();
        return;
      }
      toast.error(
        error?.response?.data?.message || "Could not refresh the lobby.",
      );
    } finally {
      setRefreshing(false);
    }
  }, [
    gameId,
    gameToken,
    refreshing,
    loadLobbyData,
    loadInboxData,
    loadCurrentTurnView,
    loadPenaltyView,
    loadMyStats,
    loadLeaderboard,
    loadMyHistory,
    handleStaleSession,
  ]);

  if (
    !gameToken ||
    (gameEntry?._id && String(gameEntry._id) !== String(gameId))
  ) {
    return <Navigate to="/game/enter" replace />;
  }

  if (loading) {
    return <div className={cls.stateScreen}>Loading lobby...</div>;
  }

  if (!lobby) {
    return <div className={cls.stateScreen}>Lobby not available.</div>;
  }

  return (
    <main className={cls.page}>
      <div className={cls.bgGlowPink} />
      <div className={cls.bgGlowBlue} />
      <div className={cls.gridLines} />

      <PartnerRequestModal
        isOpen={modalOpen}
        loading={requestSubmitting}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSendPartnerRequest}
        availablePlayers={availableSinglePlayers}
      />

      <div className={cls.pageInner}>
        <section className={cls.headerBar}>
          <button
            type="button"
            className={cls.exitButton}
            onClick={handleLogout}
            disabled={loggingOut}
          >
            <span className={cls.exitIcon}>↩</span>
            <span>{loggingOut ? "Leaving..." : "Exit"}</span>
          </button>

          <div className={cls.headerMeta}>
            <div className={cls.gameMeta}>
              <span className={cls.kicker}>LIVE GAME LOBBY</span>
              <h1 className={cls.gameTitle}>
                {lobby?.game?.title || "Game Lobby"}
              </h1>
            </div>

            <div className={cls.inlineStats}>
              <span className={cls.inlineStat}>
                <span className={cls.statIcon}>◔</span>
                <span className={cls.statText}>
                  {lobby?.game?.currentRound || 1}/{lobby?.game?.maxRounds || 1}
                </span>
              </span>

              <span className={cls.inlineStat}>
                <span className={cls.statIcon}>👥</span>
                <span className={cls.statText}>
                  {activeCount}/{totalCount}
                </span>
              </span>
            </div>
          </div>

          <button
            type="button"
            className={cls.refreshButton}
            onClick={handleRefreshLobby}
            disabled={refreshing}
          >
            <span className={cls.refreshIcon}>↻</span>
            <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
          </button>
        </section>

        {/* <GameStatusBanner liveState={lobby.liveState} /> */}
        <GameReconnectBanner
          recoveryStatus={
            recovery?.recoveryStatus ||
            (connectionStatus === "reconnecting" ? "reconnecting" : "ready")
          }
        />

        <section
          className={`${cls.myProfile} ${mobileTab === "play" ? "" : cls.tabHidden}`}
        >
          <div className={cls.myProfileGlow} />
          <div className={cls.myAvatarWrap}>
            {currentAvatar ? (
              <img
                src={currentAvatar}
                alt={currentPlayer?.displayName || "Player avatar"}
                className={cls.myAvatar}
              />
            ) : (
              <div className={cls.myAvatarFallback}>{currentInitial}</div>
            )}
          </div>

          <div className={cls.myDetails}>
            <div className={cls.nameRow}>
              <span className={cls.playerNo}>
                #{currentPlayer?.playerNo || "--"}
              </span>
              <h2 className={cls.playerName}>
                {currentPlayer?.displayName || "Unknown Player"}
              </h2>
            </div>

            <div className={cls.coinRow}>
              <span className={cls.coinLabel}>Remaining Clothes</span>
              <strong className={cls.coinValue}>
                {currentPlayer?.currentCoins ?? "--"}
              </strong>
            </div>

            <div className={cls.statusLine}>
              Your status:{" "}
              <strong>{getFriendlyPartnerLabel(currentPlayer)}</strong>
            </div>

            {currentPlayer?.partner?.displayName ? (
              <div className={cls.partnerLine}>
                Partner player #{currentPlayer.partner.playerNo}
              </div>
            ) : null}
          </div>
        </section>
        <GameMobileTabs activeTab={mobileTab} onChange={setMobileTab} />

        <section
          className={`${cls.playersSection} ${mobileTab === "play" ? "" : cls.tabHidden}`}
        >
          <div className={cls.sectionHead}>
            <div>
              <p className={cls.sectionEyebrow}>TURN STAGE</p>
              <h2 className={cls.sectionTitle}>Current Turn</h2>
            </div>
          </div>

          <GameTurnStage
            currentTurnView={currentTurnView}
            penaltyView={penaltyView}
            loading={turnLoading}
            onAnswer={handleSubmitAnswer}
            onGuess={handleSubmitGuess}
            onChoosePenaltyMode={handleChoosePenaltyMode}
            onConfirmCoinPaid={handleConfirmCoinPaid}
            onConfirmDareDone={handleConfirmDareDone}
          />
        </section>

        <section
          className={`${cls.pairingPanel} ${mobileTab === "play" ? "" : cls.tabHidden}`}
        >
          <div className={cls.panelGlow} />

          <div className={cls.panelHead}>
            <div>
              <p className={cls.panelEyebrow}>PAIRING CONTROL</p>
              <h2 className={cls.panelTitle}>My pairing state</h2>
              <p className={cls.panelText}>
                Send a partner request only when you are single and have no
                pending inbox items.
              </p>
            </div>

            <button
              type="button"
              className={cls.requestButton}
              onClick={() => setModalOpen(true)}
              disabled={!canSendRequest || requestSubmitting}
            >
              {currentPlayer?.partnerStatus === "partnered"
                ? "Already Partnered"
                : requestSubmitting
                  ? "Working..."
                  : "Request Partner"}
            </button>
          </div>

          <div className={cls.summaryGrid}>
            <article className={cls.summaryTile}>
              <span className={cls.summaryLabel}>PLAYER NO</span>
              <strong className={cls.summaryValue}>
                #{currentPlayer?.playerNo || "--"}
              </strong>
            </article>

            <article className={cls.summaryTile}>
              <span className={cls.summaryLabel}>STATE</span>
              <strong className={cls.summaryValueCaps}>
                {currentPlayer?.partnerStatus || "single"}
              </strong>
            </article>

            <article className={cls.summaryTile}>
              <span className={cls.summaryLabel}>PARTNER</span>
              <strong className={cls.summaryValue}>
                {currentPlayer?.partner?.displayName || "Not yet"}
              </strong>
            </article>
          </div>
        </section>

        <section
          className={`${cls.tabSection} ${mobileTab === "inbox" ? "" : cls.tabHidden}`}
        >
          <PartnerRequestInbox
            inbox={inbox}
            actionLoadingId={actionLoadingId}
            onAccept={(request) => handleInboxAction(request, "accept")}
            onReject={(request) => handleInboxAction(request, "reject")}
            onCancel={(request) => handleInboxAction(request, "cancel")}
          />
        </section>

        <section
          className={`${cls.playersSection} ${mobileTab === "players" ? "" : cls.tabHidden}`}
        >
          <div className={cls.sectionHead}>
            <div>
              <p className={cls.sectionEyebrow}>LOBBY SCAN</p>
              <h2 className={cls.sectionTitle}>Other Players</h2>
            </div>
            <span className={cls.sectionCount}>{otherPlayers.length}</span>
          </div>

          {otherPlayers.length ? (
            <div className={cls.playerGrid}>
              {otherPlayers.map((player) => (
                <GameLobbyPlayerCard key={player._id} player={player} />
              ))}
            </div>
          ) : (
            <div className={cls.emptyState}>
              No other players in the lobby yet.
            </div>
          )}
        </section>

        <section
          className={`${cls.playersSection} ${mobileTab === "stats" ? "" : cls.tabHidden}`}
        >
          <div className={cls.sectionHead}>
            <div>
              <p className={cls.sectionEyebrow}>PERFORMANCE</p>
              <h2 className={cls.sectionTitle}>Live Stats</h2>
            </div>
          </div>
          <div className={cls.dataPanel}>
            <MyGameStatsCard stats={myStats} />
          </div>
          <div className={cls.dataPanel}>
            <GameLeaderboardCard rows={leaderboard} />
          </div>
        </section>

        <section
          className={`${cls.playersSection} ${mobileTab === "history" ? "" : cls.tabHidden}`}
        >
          <div className={cls.sectionHead}>
            <div>
              <p className={cls.sectionEyebrow}>RECENT MEMORY</p>
              <h2 className={cls.sectionTitle}>My Turn History</h2>
            </div>
          </div>
          <div className={cls.dataPanel}>
            <GameHistoryList items={myHistory} />
          </div>
        </section>
      </div>
    </main>
  );
}
